import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { NicolAIAvatar } from '@/shared/components'

interface ChatDrawerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
}

export function ChatDrawer({ isOpen, onClose, children }: ChatDrawerProps) {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full bg-background-secondary shadow-2xl transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between border-b border-border bg-background px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-background-secondary text-foreground transition-colors hover:bg-border"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <NicolAIAvatar size="md" />
              <span className="text-base font-semibold text-foreground">NicolAI</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[calc(100%-56px)] flex-col">{children}</div>
      </div>
    </>
  )
}
